import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hook";
import { fetchSelectedPrincipal } from "../../../features/principal/PrincipalSlice";
import { RootState } from "../../../store/store";
import { Loader2 } from "lucide-react";
import { useMoney } from "../../../hooks/useMoney";
import { usePrincipalForm } from "./hooks/usePrincipalForm";
import { validatePrincipalForm, scrollToFirstError } from "./validation";
import { CommissionSlab } from "./types";
import PrincipalFormHeader from "./components/PrincipalFormHeader";
import ErrorDisplay from "./components/ErrorDisplay";
import BasicInformationForm from "./components/BasicInformationForm";
import CommissionInformationForm from "./components/CommissionInformationForm";
import VoucherLocationForm from "./components/VoucherLocationForm";
import ContactInformationForm from "./components/ContactInformationForm";
import WhitelistedIPsForm from "./components/WhitelistedIPsForm";
import AdditionalSettingsForm from "./components/AdditionalSettingsForm";
import BankDetailsForm from "./components/BankDetailsForm";
import ProcessorOnboardingForm from "./components/ProcessorOnboardingForm";
import ReviewPrincipalPage from "./ReviewPrincipalPage";
import SelfDistributorForm from "./components/SelfDistributorForm";
import { fetchDistributors } from "../../../features/distributor/DistributorSlice";

interface EditPrincipalPageProps {
  principalId: string;
  onBack: () => void;
}

const EditPrincipalPage: React.FC<EditPrincipalPageProps> = ({ principalId, onBack }) => {
  const dispatch = useAppDispatch();
  const { code } = useMoney();
  const { principalError, selectedPrincipal, selectedPrincipalLoading } = useAppSelector((state: RootState) => state.principal);
  const { distributorList, loading: distributorsLoading } = useAppSelector((state: RootState) => state.distributor);
  const [showReview, setShowReview] = useState(false);

  const {
    formData,
    formErrors,
    setFormErrors,
    setFormDataValue,
    handleChange,
    handleAddCommissionSlab,
    handleRemoveCommissionSlab,
    handleCommissionSlabChange,
    validateCommissionSlabs,
    handleAddIp,
    handleRemoveIp,
    handleAddBankAccount,
    handleRemoveBankAccount,
    handleBankAccountChange,
    handleLogoChange,
    handleContractDocumentChange,
    handleRemoveLogo,
    handleRemoveContractDocument,
  } = usePrincipalForm();

  // Fetch principal details on mount
  useEffect(() => {
    dispatch(fetchSelectedPrincipal(principalId));
    dispatch(fetchDistributors(null));
  }, [dispatch, principalId]);

  // Populate form when principal data is loaded
  useEffect(() => {
    if (selectedPrincipal) {
      const principal = selectedPrincipal as any;
      const commissionSlabs: CommissionSlab[] = principal.commissionSlabs
        ? Object.entries(principal.commissionSlabs).map(([range, percentage], index) => ({
            id: `slab-${index}`,
            range: range,
            percentage: String(percentage),
          }))
        : [];

      // Convert principalBanks from API to form structure
      const principalBanks = principal.principalBanks
        ? principal.principalBanks.map((bank: any, index: number) => ({
            id: `bank-${index}`,
            principalBankId: bank.principalBankId, // Preserve existing bank ID
            name: bank.name || "",
            accountHolderName: bank.accountHolderName || "",
            accountNo: bank.accountNo || "",
            ifscCode: bank.ifscCode || "",
            bankCity: bank.bankCity || "",
          }))
        : [];

      setFormDataValue({
        name: principal.name || "",
        principalCode: principal.principalCode || "",
        shortName: principal.shortName || "",
        logoUrl: null, // Only accept file uploads, not URLs
        merchantId: principal.merchantId || "",
        commissionType: principal.commissionType || "percentage",
        commissionPercentage: principal.commissionPercentage?.toString() || "",
        commissionFixedAmount: principal.commissionFixedAmount?.toString() || "0.00",
        commissionSlabs: commissionSlabs,
        voucherType: principal.voucherType || "erupi",
        country: principal.country || "IND",
        isDistributionOnly: principal.isDistributionOnly || false,
        paymentTerm: principal.paymentTerm || "",
        contractDocumentUrl: null, // Only accept file uploads, not URLs
        principalConversionRate: principal.principalConversionRate?.toString() || "1.0000",
        useLogo: principal.useLogo ?? false,
        redemptionNotification: principal.redemptionNotification ?? false,
        redemptionDetails: principal.redemptionDetails || "",
        codeValidityBalanceCheck: principal.codeValidityBalanceCheck ?? false,
        codeActivationFeeAmount: principal.codeActivationFeeAmount?.toString() || "0.00",
        codeActivationFeeCurrency: principal.codeActivationFeeCurrency || code,
        principalBanks: principalBanks,
        contactInformation: principal.contactInformation || {
          email: "",
          phone: "",
          contactPerson: "",
          address: {
            city: "",
            state: "",
            street: "",
            country: "",
            zipCode: "",
          },
        },
        whiteListedIps: principal.whiteListedIps || [],
        email: principal.email || "",
        password: "", // Don't pre-fill password
        addSelfDistributor: Boolean(principal.distributorId),
        distributorId: principal.distributorId ? String(principal.distributorId) : "",
      });
    }
  }, [selectedPrincipal, setFormDataValue, code]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const { hasError, errors } = validatePrincipalForm(formData, true);

    const commissionSlabs = validateCommissionSlabs();
    if (formData.commissionSlabs.length > 0 && !commissionSlabs) {
      errors.commissionSlabs = "Please fill all commission slab fields with valid values";
    }

    if (hasError || errors.commissionSlabs) {
      setFormErrors(errors);
      scrollToFirstError(errors);
      return;
    }

    // If validation passes, show review page
    setShowReview(true);
  };

  const handleReviewBack = () => {
    setShowReview(false);
  };

  const handleSubmitSuccess = () => {
    onBack();
  };

  if (selectedPrincipalLoading) {
    return (
      <div className="bg-white h-full">
        <PrincipalFormHeader title="Edit Principal" onBack={onBack} />
        <div className="flex justify-center items-center h-[calc(100vh-20rem)]">
          <div className="flex items-center gap-2">
            <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
            <span className="text-sm text-gray-500">Loading principal details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (showReview) {
    return (
      <ReviewPrincipalPage
        formData={formData}
        isEditMode={true}
        principalId={principalId}
        onBack={handleReviewBack}
        onSubmitSuccess={handleSubmitSuccess}
      />
    );
  }

  return (
    <div className="bg-white h-full">
      <PrincipalFormHeader title="Edit Principal" onBack={onBack} />

      <form onSubmit={handleSubmit} className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8 space-y-8">
        <ErrorDisplay error={principalError} id="edit_PrincipalError" />

        <BasicInformationForm
          formData={formData}
          formErrors={formErrors}
          onChange={handleChange}
          onLogoChange={handleLogoChange}
          onRemoveLogo={handleRemoveLogo}
          logoUrl={(selectedPrincipal as any)?.logoUrl || null}
        />

        <CommissionInformationForm
          formData={formData}
          formErrors={formErrors}
          onChange={handleChange}
          onAddCommissionSlab={handleAddCommissionSlab}
          onRemoveCommissionSlab={handleRemoveCommissionSlab}
          onCommissionSlabChange={handleCommissionSlabChange}
        />

        <VoucherLocationForm
          formData={formData}
          formErrors={formErrors}
          onChange={handleChange}
          onContractDocumentChange={handleContractDocumentChange}
          onRemoveContractDocument={handleRemoveContractDocument}
        />

        <ContactInformationForm
          formData={formData}
          formErrors={formErrors}
          onChange={handleChange}
        />

        <WhitelistedIPsForm
          formData={formData}
          formErrors={formErrors}
          onAddIp={handleAddIp}
          onRemoveIp={handleRemoveIp}
        />

        <AdditionalSettingsForm
          formData={formData}
          formErrors={formErrors}
          onChange={handleChange}
        />

        <BankDetailsForm
          formData={formData}
          formErrors={formErrors}
          onAddBankAccount={handleAddBankAccount}
          onRemoveBankAccount={handleRemoveBankAccount}
          onBankAccountChange={handleBankAccountChange}
          isEditMode={true}
        />

        {/*
          Processor onboarding has its own submit button and its own API
          call, entirely independent of the principal update flow below
          (per product requirement - onboarding with a processor is never
          bundled with a principal create/update submission). Its buttons
          are type="button", so rendering it inside this <form> is safe -
          it never triggers the principal update submit below.
        */}
        <ProcessorOnboardingForm
          principalId={principalId}
          principalName={formData.name}
          contactEmail={formData.contactInformation.email}
          contactPhone={formData.contactInformation.phone}
        />

        <SelfDistributorForm
          formData={formData}
          formErrors={formErrors}
          onChange={handleChange}
          onDistributorChange={(distributorId) => setFormDataValue({ distributorId })}
          distributors={distributorList || []}
          loading={distributorsLoading}
        />

        {/* Action Buttons */}
        <div className="flex justify-between pb-8">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none transition-colors"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none transition-colors"
          >
            Review & Update
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditPrincipalPage;
